import React from "react";
import EmptyState from "./EmptyState";
import { TableSkeleton } from "./Loader";

/**
 * Table — Ocean Blue Design System
 *
 * Compound component:
 *   Table              — outer wrapper with scroll and border
 *   Table.Head         — sticky thead
 *   Table.HeadCell     — th with sort support
 *   Table.Body         — tbody
 *   Table.Row          — tr with hover and status color support
 *   Table.Cell         — td
 *   Table.AmountCell   — right-aligned tabular-nums currency td
 *   Table.ActionCell   — right-aligned action icons td
 *   Table.StatusCell   — auto-sizes to badge content
 *
 * Standalone:
 *   DataTable          — full self-contained table from a data config
 */

/* ── SortIcon ────────────────────────────────────────── */
const SortIcon = ({ direction }) => (
  <span className="inline-flex flex-col ml-1.5 gap-0.5 opacity-60">
    <svg
      className={["w-2.5 h-2.5 transition-opacity", direction === "asc" ? "opacity-100" : ""].join(" ")}
      viewBox="0 0 10 6"
      fill="currentColor"
    >
      <path d="M5 0L10 6H0L5 0Z" />
    </svg>
    <svg
      className={["w-2.5 h-2.5 transition-opacity", direction === "desc" ? "opacity-100" : ""].join(" ")}
      viewBox="0 0 10 6"
      fill="currentColor"
    >
      <path d="M5 6L0 0H10L5 6Z" />
    </svg>
  </span>
);

/* ── Table (Root) ────────────────────────────────────── */
const Table = ({
  children,
  className = "",
  tableClassName = "",
  stickyHeader = true,
  ...rest
}) => {
  const classes = (className || "").split(/\s+/).filter(Boolean);
  const tableClasses = [tableClassName];
  const wrapperClasses = [];

  classes.forEach((cls) => {
    if (
      cls.startsWith("min-w-") ||
      cls.startsWith("w-[") ||
      cls.startsWith("w-max") ||
      cls.startsWith("table-") ||
      cls === "min-w-full"
    ) {
      tableClasses.push(cls);
    } else {
      wrapperClasses.push(cls);
    }
  });

  return (
    <div
      {...rest}
      className={[
        "w-full max-w-full min-w-0 overflow-auto rounded-xl border border-[#E2E8F0] flex-grow flex-1 min-h-0",
        "shadow-[0_1px_3px_rgba(15,23,42,0.06),0_1px_2px_rgba(15,23,42,0.04)]",
        ...wrapperClasses,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <table
        className={[
          "w-full min-w-full text-sm border-collapse",
          ...tableClasses,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {children}
      </table>
    </div>
  );
};
Table.displayName = "Table";

/* ── Table.Head ──────────────────────────────────────── */
const TableHead = ({ children, className = "" }) => (
  <thead
    className={[
      // Sticks BELOW whatever already occupies the top of the scroller — a tab
      // strip, a toolbar — rather than fighting it for the same strip of screen.
      // Defaults to 0px, so every screen that sets nothing behaves exactly as
      // before; Day-End measures its tab strip and sets the variable.
      "bg-[#F8FAFC] border-b border-[#E2E8F0] sticky top-[var(--table-head-offset,0px)] z-10",
      className,
    ]
      .filter(Boolean)
      .join(" ")}
  >
    {children}
  </thead>
);
TableHead.displayName = "Table.Head";

/* ── Table.HeadCell ──────────────────────────────────── */
const TableHeadCell = ({
  children,
  sortable = false,
  sortDirection,
  onSort,
  align = "left",
  className = "",
  width,
}) => {
  const alignClass = { left: "text-left", right: "text-right", center: "text-center" }[align];
  const justifyClass = { left: "justify-start", right: "justify-end", center: "justify-center" }[align];
  return (
    <th
      className={[
        "px-3 py-2 text-[11px] font-semibold text-[#475569]",
        "uppercase tracking-[0.06em] whitespace-nowrap select-none",
        alignClass,
        sortable ? "cursor-pointer hover:text-[#0F172A] hover:bg-[#F1F5F9] transition-colors duration-150" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={sortable ? onSort : undefined}
      style={width ? { width } : undefined}
      scope="col"
    >
      <span className={["inline-flex items-center gap-1 w-full", justifyClass].join(" ")}>
        {children}
        {sortable && <SortIcon direction={sortDirection} />}
      </span>
    </th>
  );
};
TableHeadCell.displayName = "Table.HeadCell";

/* ── Table.Body ──────────────────────────────────────── */
const TableBody = ({ children, className = "" }) => (
  <tbody className={className}>{children}</tbody>
);
TableBody.displayName = "Table.Body";

/* ── Table.Row ───────────────────────────────────────── */
const ROW_STATUS = {
  received: "bg-white",
  pending: "bg-white",
  rejected: "bg-white",
  default: "bg-white",
};

const TableRow = ({
  children,
  status,
  onClick,
  isSelected = false,
  className = "",
}) => {
  const statusClass = ROW_STATUS[status] ?? ROW_STATUS.default;
  return (
    <tr
      onClick={onClick}
      className={[
        "border-b border-[#F1F5F9] last:border-b-0 transition-colors duration-150",
        statusClass,
        onClick ? "cursor-pointer" : "",
        isSelected ? "ring-2 ring-inset ring-[#004BB5]/20 bg-[#E8F0FE]" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </tr>
  );
};
TableRow.displayName = "Table.Row";

/* ── Table.Cell ──────────────────────────────────────── */
const TableCell = ({
  children,
  align = "left",
  className = "",
  colSpan,
  "data-label": dataLabel,
}) => {
  const alignClass = { left: "text-left", right: "text-right", center: "text-center" }[align];
  return (
    <td
      className={[
        "px-3 py-1.5 text-sm text-[#1E293B] font-normal whitespace-nowrap",
        alignClass,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      colSpan={colSpan}
      data-label={dataLabel}
    >
      {children}
    </td>
  );
};
TableCell.displayName = "Table.Cell";

/* ── Table.AmountCell ────────────────────────────────── */
const TableAmountCell = ({ children, prefix = "₹", align = "left", className = "", negative = false }) => {
  const alignClass = { left: "text-left", right: "text-right", center: "text-center" }[align];
  return (
    <td
      className={[
        "px-3 py-1.5 text-sm font-semibold tabular-nums whitespace-nowrap font-data",
        alignClass,
        negative ? "text-[#DC2626]" : "text-[#0F172A]",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children !== undefined && children !== null && children !== "" ? (
        <>
          <span className="text-[#94A3B8] font-normal mr-0.5 text-xs">{prefix}</span>
          {typeof children === "number"
            ? children.toLocaleString("en-IN", { minimumFractionDigits: 0 })
            : children}
        </>
      ) : (
        <span className="text-[#CBD5E1]">—</span>
      )}
    </td>
  );
};
TableAmountCell.displayName = "Table.AmountCell";

/* ── Table.ActionCell ────────────────────────────────── */
const TableActionCell = ({ children, align = "left", className = "" }) => {
  const justifyClass = { left: "justify-start", right: "justify-end", center: "justify-center" }[align];
  return (
    <td
      className={[
        "px-3 py-1 whitespace-nowrap",
        className,
      ].join(" ")}
      data-label="Actions"
    >
      <div className={["flex items-center gap-1.5", justifyClass].join(" ")}>{children}</div>
    </td>
  );
};
TableActionCell.displayName = "Table.ActionCell";

/* ── Table.EmptyRow ──────────────────────────────────── */
const TableEmptyRow = ({ colSpan = 1, message, action }) => (
  <tr>
    <td colSpan={colSpan} className="p-0">
      <EmptyState
        variant="search"
        title={message ?? "No records found"}
        description="Try adjusting your filters or search terms."
        action={action}
        size="sm"
      />
    </td>
  </tr>
);
TableEmptyRow.displayName = "Table.EmptyRow";

/* ── Attach subcomponents ────────────────────────────── */
Table.Head = TableHead;
Table.HeadCell = TableHeadCell;
Table.Body = TableBody;
Table.Row = TableRow;
Table.Cell = TableCell;
Table.AmountCell = TableAmountCell;
Table.ActionCell = TableActionCell;
Table.EmptyRow = TableEmptyRow;

/* ────────────────────────────────────────────────────────
   DataTable — convenience wrapper for config-driven tables
   ──────────────────────────────────────────────────────── */

/**
 * DataTable
 *
 * columns: Array<{
 *   key       : string,
 *   label     : string,
 *   align?    : "left"|"right"|"center",
 *   width?    : string,
 *   sortable? : boolean,
 *   render?   : (value, row, index) => ReactNode,
 *   type?     : "amount" | "action" | "text"
 * }>
 *
 * data        : Array<object>
 * rowKey      : string | ((row) => string)
 * isLoading   : boolean
 * onRowClick  : (row) => void
 * emptyMessage: string
 * sortConfig  : { key, direction: "asc"|"desc" }
 * onSort      : (key) => void
 */
export const DataTable = ({
  columns = [],
  data = [],
  rowKey = "id",
  isLoading = false,
  onRowClick,
  emptyMessage,
  sortConfig,
  onSort,
  className = "",
}) => {
  if (isLoading) return <TableSkeleton rows={6} cols={columns.length} className={className} />;

  return (
    <Table className={className}>
      <Table.Head>
        <tr>
          {columns.map((col) => (
            <Table.HeadCell
              key={col.key}
              align={col.align ?? "left"}
              width={col.width}
              sortable={col.sortable}
              sortDirection={sortConfig?.key === col.key ? sortConfig.direction : undefined}
              onSort={() => onSort?.(col.key)}
            >
              {col.label}
            </Table.HeadCell>
          ))}
        </tr>
      </Table.Head>

      <Table.Body>
        {data.length === 0 ? (
          <Table.EmptyRow colSpan={columns.length} message={emptyMessage} />
        ) : (
          data.map((row, idx) => {
            const key = typeof rowKey === "function" ? rowKey(row) : row[rowKey] ?? idx;
            return (
              <Table.Row key={key} onClick={onRowClick ? () => onRowClick(row) : undefined} status={row._status}>
                {columns.map((col) => {
                  const value = row[col.key];
                  if (col.type === "amount") {
                    return (
                      <Table.AmountCell key={col.key} negative={value < 0}>
                        {Math.abs(value)}
                      </Table.AmountCell>
                    );
                  }
                  if (col.type === "action") {
                    return (
                      <Table.ActionCell key={col.key}>
                        {col.render?.(value, row, idx)}
                      </Table.ActionCell>
                    );
                  }
                  return (
                    <Table.Cell key={col.key} align={col.align}>
                      {col.render ? col.render(value, row, idx) : (value ?? "—")}
                    </Table.Cell>
                  );
                })}
              </Table.Row>
            );
          })
        )}
      </Table.Body>
    </Table>
  );
};

export default Table;

